import { USER_ROLES, UserUtilities } from '@pwc/pwc-deals-web-common';
import decode from 'jwt-decode';
import featureFlags from 'common/helpers/featureFlags';

export const CLIENT_TOKEN_KEY = `${USER_ROLES.CLIENT}_TOKEN_`;
export const PWC_USER_TOKEN_KEY = `${USER_ROLES.PWC_USER}_TOKEN_`;

const jwtTokenKey = 'jwtToken';
const refreshTokenKey = 'refreshToken';
const processTimeKey = 'processTime';
const getToken = () => {
  const jwtTokenValue = sessionStorage.getItem(jwtTokenKey);

  if (!jwtTokenValue) return null;

  return decode(jwtTokenValue);
};

const removeToken = () => {
  sessionStorage.clear();
};

const secondsFromExp = () => {
  const jwtToken = getToken();
  return jwtToken && jwtToken.exp - Date.now() / 1000;
};

const hasValidToken = () => {
  const jwtToken = getToken();
  if (!jwtToken) return false;
  const secondsUntilTokenExpires =
    jwtToken && Math.floor(jwtToken.exp - Date.now() / 1000);

  if (secondsUntilTokenExpires > 0) return true;

  removeToken();
  return false;
};

const hasValidRefreshToken = () => {
  const jwt = sessionStorage.getItem(refreshTokenKey);
  if (!jwt) return false;

  const token = decode(jwt);
  const secondsUntilTokenExpires =
    token && Math.floor(token.exp - Date.now() / 1000);

  if (secondsUntilTokenExpires > 0) return true;

  // Clean up the refresh token if it's expired
  sessionStorage.removeItem(refreshTokenKey);
  return false;
};

const setToken = async (token) => {
  sessionStorage.setItem(jwtTokenKey, token);
};

const storageKeyTemplatePrefix = 'projectJWT';
const storageKeyTemplate = `${storageKeyTemplatePrefix}{PROJ}`;

const setTokens = async (tokenSet) => {
  if (featureFlags.isUserAccessTokenV2Enabled) {
    sessionStorage.setItem(jwtTokenKey, tokenSet.masterToken);
    sessionStorage.setItem(refreshTokenKey, tokenSet.refreshToken);
  } else {
    sessionStorage.setItem(jwtTokenKey, tokenSet.applicationToken);
  }
  if (tokenSet.projectTokens) {
    tokenSet.projectTokens.forEach((projectToken) => {
      const tokenStorageKey = storageKeyTemplate.replace(
        '{PROJ}',
        btoa(projectToken.projectId)
      );

      if (UserUtilities.isActualPwcUser()) {
        sessionStorage.setItem(tokenStorageKey, projectToken.token);
        if (featureFlags.isGlobalToggleEnabled) {
          sessionStorage.setItem(
            `${CLIENT_TOKEN_KEY}${tokenStorageKey}`,
            projectToken.clientToken
          );
          sessionStorage.setItem(
            `${PWC_USER_TOKEN_KEY}${tokenStorageKey}`,
            projectToken.token
          );
        }
      } else {
        sessionStorage.setItem(tokenStorageKey, projectToken.token);
        if (featureFlags.isGlobalToggleEnabled) {
          sessionStorage.setItem(
            `${CLIENT_TOKEN_KEY}${tokenStorageKey}`,
            projectToken.token
          );
        }
      }
    });
  }
};

const updateProcessTime = () => {
  sessionStorage.setItem(processTimeKey, Date.now() / 1000);
};

const getEmail = () => {
  const token = getToken();
  return token && token.email ? token.email : null;
};

const getTokenValue = (key) => {
  const token = getToken();
  return token && token[key] ? token[key] : null;
};

const getExpirationInSeconds = () => {
  const expirationInMinutesEnvVar =
    process.env.REACT_APP_SECURITY_TOKEN_EXPIRATION_MINUTES;

  const paddingInMinutes = 2;

  if (expirationInMinutesEnvVar) {
    const parsedExpirationInMinutes = parseInt(expirationInMinutesEnvVar, 10);
    if (parsedExpirationInMinutes > paddingInMinutes) {
      return (parsedExpirationInMinutes - paddingInMinutes) * 60;
    }
  }

  return (15 - paddingInMinutes) * 60;
};

const canCreateProjects = () => {
  const token = getToken();
  return token && token['Create Project'] !== undefined;
};

const authorizationService = {
  secondsFromExp,
  hasValidToken,
  hasValidRefreshToken,
  setToken,
  setTokens,
  getToken,
  getTokenValue,
  updateProcessTime,
  getEmail,
  getExpirationInSeconds,
  canCreateProjects,
};

export default authorizationService;
